import { CHANGE_ESTADO_EPP, CREATE_EPP, UPDATE_EPP } from "@/apollo/mutations";
import { LIST_CATEGORIAS_EPP } from "@/apollo/queries";
import {
  GET_FORMULARIO_BY_TIPO_ADMIN,
  GET_PERFIL_ENCARGADO,
  LIST_BODEGAS_EMPRESA,
  LIST_BODEGAS_EMPRESA_CON_SEGMENTACION,
  LIST_EPPS,
  LIST_EPPS_SIMPLE,
  PAGINACION_EPPS,
} from "@/apollo/queries/epp";
import { exMut, exQue } from "@/utils/gql";
import {
  BODEGAS_CON_SUCURSALES_Y_SOCIEDADES,
  normalizarBodega,
} from "./Bodega";
import Vue from "vue";

// Pestañas de EPP por rol del encargado (GetPerfilEncargado): el administrador
// local ve dashboard, solicitudes y reportes; el pañolero, solicitudes, EPP y
// reportes. El superadministrador ve todo y no está en la tabla. Cada rol sale de
// los canView* de .SolicitudInventario/controllers/encargadoHelper.js: el
// comentario de cabecera de ese archivo dice que el pañolero no ve reportes, pero
// su canViewReportes es true. El mensaje de ayuda del switch "Supervisor"
// (Encargados/EncargadosModal.vue) repite esto: si cambia la tabla, cambia también.
const RUTAS_EPP_POR_ROL = {
  administrador_local: ["epp-Dashboard", "epp-Solicitudes", "epp-Reportes"],
  panolero: ["epp-Solicitudes", "epp-Epp", "epp-crear", "epp-editar", "epp-Reportes"],
};
// Sin rol de encargado el backend devuelve todo vacío; se deja Solicitudes
const RUTAS_EPP_SIN_ROL = ["epp-Solicitudes"];
// privilegio[0].id_tipo del usuario (getter loginModule/idPrivilegio)
const PRIVILEGIO_FUNDADOR = 1;

// Toast desde el store, que no tiene los helpers del mixin global (mismo
// canal que utils/gql.js)
const toastError = (message) => {
  Vue.$toast.open({
    message,
    type: "error",
    duration: 5000,
    position: "top-right",
  });
};

// Errores que se detectan antes de llamar al backend. Los del backend ya los
// avisa exMut.
const errorAntesDeGuardar = (payload, formEpp) => {
  const idCategoria = Number(payload?.id_categoria);
  if (!Number.isInteger(idCategoria) || idCategoria <= 0) {
    return "Debes seleccionar una categoría válida.";
  }
  if (!formEpp?.id_formulario) {
    return "No se pudo cargar el formulario de EPP de la empresa. Recarga la página e inténtalo de nuevo.";
  }
  return null;
};

const normalizeInventario = (inventario = []) => {
  if (!Array.isArray(inventario)) return [];

  const groupedBySkuTalla = new Map();

  inventario.forEach((item) => {
    const sku = String(item?.sku || "").trim();
    const talla = String(item?.talla || "").trim();
    const idBodega = Number(item?.bodega);
    const lote = String(item?.lote || "").trim();
    const stock = Number(item?.stock);
    const fechaVencimiento = item?.fecha_vencimiento
      ? String(item.fecha_vencimiento).trim()
      : null;

    const isValidItem =
      sku &&
      talla &&
      Number.isInteger(idBodega) &&
      idBodega > 0 &&
      lote &&
      Number.isInteger(stock) &&
      stock >= 0;

    if (!isValidItem) return;

    const groupKey = `${sku}__${talla}`;
    if (!groupedBySkuTalla.has(groupKey)) {
      groupedBySkuTalla.set(groupKey, {
        sku,
        talla,
        bodegasMap: new Map(),
      });
    }

    const group = groupedBySkuTalla.get(groupKey);
    if (!group.bodegasMap.has(idBodega)) {
      group.bodegasMap.set(idBodega, {
        id_bodega: idBodega,
        lotes: [],
      });
    }

    group.bodegasMap.get(idBodega).lotes.push({
      lote,
      stock,
      fecha_vencimiento: fechaVencimiento,
    });
  });

  return Array.from(groupedBySkuTalla.values()).map((group) => ({
    sku: group.sku,
    talla: group.talla,
    bodegas: Array.from(group.bodegasMap.values()),
  }));
};

// Descripción, imagen y periodicidad son opcionales: vacías van como null.
// imagen_b64 es el base64 sin "data:" o, al editar sin cambiarla, la URL actual.
// periodicidad va en horas enteras y siempre se envía: al editar, si no llega,
// el backend la deja en null.
const datosOpcionalesEpp = (payload = {}) => ({
  descripcion: String(payload.descripcion || "").trim() || null,
  imagen_b64: payload.imagen_b64 || null,
  formato_b64: payload.formato_b64 || null,
  periodicidad:
    Number(payload.periodicidad) > 0 ? Math.round(Number(payload.periodicidad)) : null,
});

export const EppState = {
  creatingEpp: false,
  createdEpp: null,
  eppIds: [],
  eppPages: [],
  epps: [],
  bodegasEmpresa: [],
  loadingEpps: false,
  categoriasEpp: [],
  eppPagination: {
    currentPage: 1,
    pageSize: 20,
    search: "",
    pageCount: 0,
  },
  /*
  formEpp: {
    id_formulario: Number,
    nombre: String,
    descripcion: String,
    has_lotes: Boolean,
    tipo_formulario: {
      id_tipo: Number,
    },
    integracion: {
      id_tipo: Number,
      nombre: String,
      is_automatico: Boolean,
    },
  }
  */
  formEpp: null,
  // { id_usuario, rol } del usuario logueado; rol null si no se pudo leer
  perfilEncargado: null,
};

export const EppMutations = {
  SET_FORM_CATEGORIA_EPP(state, response) {
    response.id_formulario = Number(response?.id_formulario) || null;
    state.formEpp = response || null;
  },
  SET_PERFIL_ENCARGADO(state, perfil) {
    state.perfilEncargado = perfil;
  },
  SET_CATEGORIAS_EPP(state, response) {
    state.categoriasEpp = response || [];
  },
  SET_CREATING_EPP(state, value) {
    state.creatingEpp = value;
  },
  SET_CREATED_EPP(state, epp) {
    state.createdEpp = epp;
    if (!epp) return;

    state.epps = [epp, ...state.epps];
  },
  setEpps(state, epps) {
    state.epps = Array.isArray(epps) ? [...epps] : [];
  },
  SET_EPP_IDS(state, ids) {
    state.eppIds = ids;
  },
  SET_EPP_PAGES(state, pages) {
    state.eppPages = pages;
  },
  SET_EPP_LOADING(state, value) {
    state.loadingEpps = value;
  },
  SET_UPDATED_EPP(state, updatedEpp) {
    if (!updatedEpp || !updatedEpp.codigo_producto) return;

    const newEpps = state.epps.filter(
      (epp) => epp.codigo_producto !== updatedEpp.codigo_producto,
    );
    state.epps = [updatedEpp, ...newEpps];
  },
  SET_EDITED_EPP(state, editedEpp) {
    if (!editedEpp || !editedEpp.codigo_producto) return;

    const index = state.epps.findIndex(
      (epp) => epp.codigo_producto === editedEpp.codigo_producto,
    );
    if (index !== -1) {
      const current = state.epps[index] || {};
      state.epps.splice(index, 1, { ...current, ...editedEpp });
    }
  },
  DELETED_EPP(state, deletedEpp) {
    if (!deletedEpp || !deletedEpp.codigo_producto) return;

    state.epps = state.epps.filter(
      (epp) => epp.codigo_producto !== deletedEpp.codigo_producto,
    );
  },
  SET_BODEGAS_EMPRESA(state, bodegas) {
    state.bodegasEmpresa = bodegas;
  },
  SET_EPP_PAGINATION(state, payload = {}) {
    state.eppPagination = { ...state.eppPagination, ...payload };
  },
};

export const EppActions = {
  async getFormCategoriaEpp({ commit }) {
    await exQue(GET_FORMULARIO_BY_TIPO_ADMIN).then((res) => {
      commit("SET_FORM_CATEGORIA_EPP", res || []);
    });
  },

  // Casi todas las consultas de EPP necesitan id_formulario: se carga el
  // formulario solo si todavía no está en el estado.
  async ensureFormEpp({ state, dispatch }) {
    if (state.formEpp?.id_formulario == null) {
      await dispatch("getFormCategoriaEpp");
    }
    return state.formEpp;
  },

  // Rol del usuario en EPP. Se pide una vez por usuario (el store no se limpia al
  // cerrar sesión). Si la consulta falla, por ejemplo con un backend que aún no
  // la tiene, el rol queda null y se ve todo, como antes: el backend igual
  // filtra lo que devuelve según el rol.
  async ensurePerfilEncargado({ state, commit, dispatch, rootGetters }) {
    const idUsuario = rootGetters["loginModule/idUsuario"];
    if (state.perfilEncargado?.id_usuario === idUsuario) return state.perfilEncargado;

    let rol = null;
    try {
      await dispatch("ensureFormEpp");
      const perfil = await exQue(
        GET_PERFIL_ENCARGADO,
        { id_formulario: state.formEpp?.id_formulario },
        true,
      );
      rol = perfil?.rol || null;
    } catch {
      // exQue ya dejó el error en consola
    }
    commit("SET_PERFIL_ENCARGADO", { id_usuario: idUsuario, rol });
    return state.perfilEncargado;
  },

  async getListCategoriasEpp({ commit, state, dispatch }) {
    await dispatch("ensureFormEpp");

    exQue(
      LIST_CATEGORIAS_EPP,
      { id_formulario: state.formEpp?.id_formulario },
      true,
    )
      .then((res) => {
        if (Array.isArray(res)) {
          commit("SET_CATEGORIAS_EPP", res);
        } else {
          commit("SET_CATEGORIAS_EPP", []);
        }
      })
      .catch((e) => {
          console.error(e);
        commit("SET_CATEGORIAS_EPP", []);
      });
  },
  async createEppAction({ commit, state, dispatch }, payload = {}) {
    commit("SET_CREATING_EPP", true);

    let response = null;
    try {
      await dispatch("ensureFormEpp").catch(() => null);
      const errorPrevio = errorAntesDeGuardar(payload, state.formEpp);
      if (errorPrevio) {
        toastError(errorPrevio);
        return null;
      }

      const variables = {
        id_categoria: Number(payload.id_categoria),
        nombre_epp: String(payload.nombre || "").trim(),
        limite: Number(payload.limite),
        precio: Number(payload.precio),
        ...datosOpcionalesEpp(payload),
        inventario: normalizeInventario(payload.inventario),
        id_formulario: state.formEpp.id_formulario,
      };

      response = await exMut(
        CREATE_EPP,
        variables,
        "EPP creada correctamente",
        "No se pudo crear la EPP",
      );

      commit("SET_CREATED_EPP", response);

      if (response) {
        await dispatch("getPages", {
          forceRefresh: true,
          pageSize: state.eppPagination.pageSize,
          initialPage: 1,
          nombre: state.eppPagination.search,
        });
      }
    } catch (error) {
      console.error(error);
      commit("SET_CREATED_EPP", null);
    } finally {
      commit("SET_CREATING_EPP", false);
    }

    return response;
  },

  async updateEppAction({ commit, state, dispatch }, payload = {}) {
    commit("SET_CREATING_EPP", true);

    let response = null;
    try {
      await dispatch("ensureFormEpp").catch(() => null);
      // Sin categoría válida no se actualiza (no se adivina una)
      const errorPrevio = errorAntesDeGuardar(payload, state.formEpp);
      if (errorPrevio) {
        toastError(errorPrevio);
        return null;
      }

      const variables = {
        codigo_producto: Number(payload.codigo_producto),
        id_categoria: Number(payload.id_categoria),
        nombre_epp: String(payload.nombre || "").trim(),
        limite: Number(payload.limite),
        precio: Number(payload.precio),
        ...datosOpcionalesEpp(payload),
        inventario: normalizeInventario(payload.inventario),
        id_formulario: state.formEpp.id_formulario,
      };

      response = await exMut(
        UPDATE_EPP,
        variables,
        "EPP actualizada correctamente",
        "No se pudo actualizar la EPP",
      );

      commit("SET_UPDATED_EPP", response);

      if (response) {
        await dispatch("getPages", {
          forceRefresh: true,
          pageSize: state.eppPagination.pageSize,
          initialPage: state.eppPagination.currentPage || 1,
          nombre: state.eppPagination.search,
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      commit("SET_CREATING_EPP", false);
    }

    return response;
  },

  async getAllSimpleEppIds(
    { commit, state, dispatch },
    { nombre = "", forceRefresh = false },
  ) {
    await dispatch("ensureFormEpp");
    if (!forceRefresh && state.eppIds && state.eppIds.length)
      return state.eppIds;

    const paginacionEpp = await exQue(
      PAGINACION_EPPS,
      {
        idFormulario: state.formEpp.id_formulario,
        limite: 200,
        nombre,
      },
      forceRefresh,
    );

    const ids = Array.isArray(paginacionEpp)
      ? paginacionEpp.flatMap((item) => item.epps || [])
      : [];

    commit("SET_EPP_IDS", ids);
    return ids;
  },

  // Todas las EPP (nombre y código) para un selector, como el de Automatización.
  // No lee ni pisa eppIds ni epps: son de la lista de EPP, y eppIds puede ser el
  // resultado de una búsqueda por nombre.
  async listarEppsParaSelector({ dispatch, state }) {
    await dispatch("ensureFormEpp");
    const paginacionEpp = await exQue(
      PAGINACION_EPPS,
      { idFormulario: state.formEpp.id_formulario, limite: 200, nombre: "" },
      true,
    );
    const ids = Array.isArray(paginacionEpp)
      ? paginacionEpp.flatMap((item) => item.epps || [])
      : [];
    if (!ids.length) return [];

    const epps = await exQue(LIST_EPPS_SIMPLE, { ids }, true);
    return Array.isArray(epps) ? epps : [];
  },

  // Para la ruta epp-editar: ListaEPP recibe códigos de producto
  async getEppByCodigo(_, codigo_producto) {
    const epps = await exQue(
      LIST_EPPS,
      { ids: [Number(codigo_producto)] },
      true,
    );
    return Array.isArray(epps) && epps.length ? epps[0] : null;
  },

  async getEppData({ commit }, { ids = [], networkOnly = false } = {}) {
    if (!Array.isArray(ids) || ids.length === 0) {
      commit("setEpps", []);
      return [];
    }

    const epps = await exQue(
      LIST_EPPS,
      {
        ids,
      },
      networkOnly,
    );

    commit("setEpps", epps || []);
    return epps || [];
  },

  async getPages({ commit, dispatch, state }, options = {}) {
    const {
      forceRefresh = false,
      pageSize = state.eppPagination.pageSize,
      initialPage = 1,
      nombre = "",
    } = options;

    commit("SET_EPP_LOADING", true);
    try {
      await dispatch("getAllSimpleEppIds", { nombre: nombre, forceRefresh });

      const pages = [];
      for (let i = 0; i < state.eppIds.length; i += pageSize) {
        pages.push(state.eppIds.slice(i, i + pageSize));
      }

      commit("SET_EPP_PAGES", pages);
      commit("SET_EPP_PAGINATION", {
        currentPage: Number(initialPage) || 1,
        pageSize,
        search: nombre,
        pageCount: pages.length,
      });

      const firstPageIndex = Math.max(Number(initialPage) - 1, 0);
      const firstIds = pages[firstPageIndex] || [];
      await dispatch("getEppData", {
        ids: firstIds,
        networkOnly: forceRefresh,
      });

      return pages.length;
    } finally {
      commit("SET_EPP_LOADING", false);
    }
  },

  async changeEppPage({ commit, dispatch, state }, page = 1) {
    const pageValue = Number(page);
    const pageNumber = Number.isFinite(pageValue)
      ? pageValue
      : Number(page?.page);
    const networkOnly = Boolean(page?.networkOnly);
    const pageIndex = Math.max(Number(pageNumber) - 1, 0);
    const ids = state.eppPages[pageIndex] || [];

    commit("SET_EPP_LOADING", true);
    try {
      await dispatch("getEppData", {
        ids,
        networkOnly,
      });
      commit("SET_EPP_PAGINATION", { currentPage: pageNumber });
      return ids;
    } finally {
      commit("SET_EPP_LOADING", false);
    }
  },

  async changeEppStatus({ commit }, { id_estado, codigo_producto }) {
    // `ChangeEstatusProducto` con `id_estado = 1` (`Habilitar`)
    // `ChangeEstatusProducto` con `id_estado = 2` (`Eliminar`)
    // `ChangeEstatusProducto` con `id_estado = 3` (`Deshabilitar`)

    const mensajes = {
      1: "EPP habilitada correctamente",
      2: "EPP eliminada correctamente",
      3: "EPP deshabilitada correctamente",
    };
    const response = await exMut(
      CHANGE_ESTADO_EPP,
      { id_estado, codigo_producto },
      mensajes[id_estado] || "",
      "No se pudo cambiar el estado de la EPP",
    );

    if (response) {
      if (id_estado === 1 || id_estado === 3)
        commit("SET_EDITED_EPP", response);
    }

    return response;
  },

  // Bodegas que el usuario puede ver, para los filtros del módulo. Con
  // segmentación se piden también sus sociedades y sucursales: de ahí salen las
  // opciones de esos dos filtros en el Dashboard, y así solo se ofrecen las que
  // tienen alguna bodega a la vista (elegir una sin bodegas daría un dashboard
  // vacío). Es la misma forma que usa getBodegas de Bodega.js.
  async listBodegasEmpresa({ commit }) {
    const response = await exQue(
      BODEGAS_CON_SUCURSALES_Y_SOCIEDADES
        ? LIST_BODEGAS_EMPRESA_CON_SEGMENTACION
        : LIST_BODEGAS_EMPRESA,
    );
    const lista = Array.isArray(response) ? response : [];
    commit(
      "SET_BODEGAS_EMPRESA",
      BODEGAS_CON_SUCURSALES_Y_SOCIEDADES ? lista.map(normalizarBodega) : lista,
    );
  },
};

export const EppGetters = {
  // Rutas de EPP que puede abrir el usuario según su rol, o null si puede abrir
  // todas (fundador, superadministrador, o rol aún sin leer). Las pestañas se
  // filtran aquí y las rutas se cierran en routesEpp.js
  rutasEppPermitidas(state, getters, rootState, rootGetters) {
    // El fundador (privilegio 1 del login) ve todo, aunque como encargado tenga
    // otro rol
    if (Number(rootGetters["loginModule/idPrivilegio"]) === PRIVILEGIO_FUNDADOR) return null;
    const rol = state.perfilEncargado?.rol;
    if (!rol || rol === "superadministrador") return null;
    return RUTAS_EPP_POR_ROL[rol] || RUTAS_EPP_SIN_ROL;
  },
  // Configuración, Encargados, Jefes, Centro de Costos y Automatización
  esSuperadminEpp(state, getters) {
    return getters.rutasEppPermitidas === null;
  },
  puedeCrearEpp(state, getters) {
    return getters.esSuperadminEpp || getters.rutasEppPermitidas.includes("epp-crear");
  },

  tab_componentes(state, getters) {
    let tabs = [
      {
        name: "Dashboard",
        route: "epp-Dashboard",
      },
      {
        name: "Solicitudes",
        route: "epp-Solicitudes",
      },
      {
        name: "EPP",
        route: "epp-Epp",
      },
      {
        name: "Crear EPP",
        route: "epp-crear",
        routeSecondary: ["epp-editar"],
      },
      {
        name: "Bodegas",
        route: "epp-Bodegas",
      },
      // Los correos a encargados no dependen de la gestión de lotes
      {
        name: "Encargados",
        route: "epp-Encargados",
      },
    ];
    if (state.formEpp?.has_lotes === true) {
      tabs = [
        ...tabs,
        {
          name: "Jefes",
          route: "epp-Jefes",
        },
        {
          name: "Centro de Costos",
          route: "epp-CentroDeCostos",
        },
      ];
    }
    // Sin las automatizaciones activadas en la configuración (Dashboard) no hay
    // pestaña; routesEpp.js cierra además la entrada directa por URL
    if (getters.automatizacionesActivas) {
      tabs.push({
        name: "Automatización",
        route: "epp-Automatizacion",
      });
    }

    tabs.push({
      name: "Reportes",
      route: "epp-Reportes",
    });

    const rutas = getters.rutasEppPermitidas;
    return rutas ? tabs.filter((tab) => rutas.includes(tab.route)) : tabs;
  },
};
